import {
  getPostalFormTypeLabel,
  POSTAL_FORM_CONTENT_DESCRIPTION,
  POSTAL_FORM_DELIVERY_WINDOWS,
  POSTAL_FORM_PAYERS,
  POSTAL_FORM_SENDER,
  POSTAL_FORM_TYPES,
} from '../constants/postalFormConstants';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const trimValue = (value) => (typeof value === 'string' ? value.trim() : '');

// Primalac se ne izvlaci iz imena pacijenta, nego iz polja o primaocu posiljke.
// Adresa se uzima sa kartona pacijenta jer posiljka ide na tu adresu.
export const buildPostalFormRecipient = (user) => ({
  fullName: trimValue(user?.shipmentRecipientFullName),
  street: trimValue(user?.address?.street),
  postalCodeAndCity: [
    trimValue(user?.address?.postalCode),
    trimValue(user?.address?.city),
  ]
    .filter(Boolean)
    .join(' '),
  phone: trimValue(user?.shipmentRecipientPhone),
});

// Posiljalac se bira iz padajuce liste (senders); mapiramo pun Sender objekat u
// oblik reda obrasca. Kad nista nije izabrano, koristi se podrazumevani
// (hardkodovani) posiljalac da se zadrzi postojece ponasanje.
export const buildPostalFormSender = (sender) => {
  if (!sender) return POSTAL_FORM_SENDER;
  return {
    fullName: trimValue(sender.fullName),
    street: trimValue(sender.street),
    postalCodeAndCity: [trimValue(sender.postalCode), trimValue(sender.city)]
      .filter(Boolean)
      .join(' '),
    phone: trimValue(sender.phone),
    bankAccount: trimValue(sender.bankAccount),
  };
};

const partyRows = (party) => [
  { label: 'Ime i Prezime', value: party?.fullName },
  { label: 'Ulica', value: party?.street },
  {
    label: 'Poštanski br./Grad',
    value: party?.postalCodeAndCity,
    smallLabel: true,
  },
  { label: 'Kontakt', value: party?.phone },
];

const fieldRow = ({ label, value, smallLabel, centered }) => `
      <tr>
        <td class="label${smallLabel ? ' label--small' : ''}">${escapeHtml(label)}</td>
        <td class="value${centered ? ' value--centered' : ''}">${escapeHtml(value)}</td>
        <td class="mark"></td>
      </tr>`;

// Blok "Posebne usluge" ide samo na obrascu SA OTKUPNINOM. Otkupnina se
// upisuje rucno u modalu, a ziro racun se vuce iz izabranog posiljaoca.
const specialServicesRows = ({ otkupninaValue, sender }) => [
  sectionRow('Posebne usluge', 3),
  fieldRow({ label: 'Vrednost', value: '' }),
  fieldRow({ label: 'Otkupnina', value: otkupninaValue, centered: true }),
  `
      <tr>
        <td class="label">Nalog za uplatu</td>
        <td class="value value--centered">DA&emsp;&emsp;&emsp;&emsp;NE</td>
        <td class="mark"></td>
      </tr>`,
  fieldRow({
    label: 'Žiro račun',
    value: sender?.bankAccount,
    centered: true,
  }),
  fieldRow({ label: 'Poziv na broj', value: '' }),
];

const sectionRow = (title, span) => `
      <tr>
        <td class="section" colspan="${span}">${escapeHtml(title)}</td>
        ${span === 3 ? '' : '<td class="mark"></td>'}
      </tr>`;

const mark = (isChecked) => (isChecked ? 'X' : '');

const documentStyles = `
    /* Margina 0 na @page: bez prostora za zaglavlje/podnozje pretrazivaca (broj
       strane, datum, URL), pa se ti tekstovi po uglovima ne stampaju. Margine
       lista dolaze iz paddinga .page. */
    @page {
      size: A4 landscape;
      margin: 0;
    }

    * {
      box-sizing: border-box;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #000000;
      font-family: Arial, Helvetica, sans-serif;
    }

    .page {
      width: 297mm;
      height: 210mm;
      padding: 10mm;
    }

    /* Obrazac zauzima prvu (gornju) polovinu stranice: 105mm papira - 10mm margine. */
    .page__top-half {
      height: 95mm;
    }

    /* Tabela levo, napomena (ako postoji) desno. */
    .layout {
      display: flex;
      align-items: flex-start;
    }

    table.postal-form {
      width: 140mm;
      flex: 0 0 auto;
      border-collapse: collapse;
      table-layout: fixed;
      border: 1.5pt solid #000000;
    }

    table.postal-form td {
      height: 6mm;
      padding: 0 1.5mm;
      border: 0.75pt solid #000000;
      font-size: 12pt;
      line-height: 1.15;
      vertical-align: middle;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    td.section {
      text-align: center;
      font-weight: bold;
      font-size: 12.5pt;
    }

    td.label--small {
      font-weight: bold;
      font-size: 9pt;
    }

    td.mark {
      text-align: center;
      font-weight: bold;
    }

    td.value--centered {
      text-align: center;
    }

    /* Napomena je odmaknuta od tabele (prostor za secenje), ali dovoljno blizu
       da bude vidljiva; uokvirena je da se jasno izdvoji. */
    .note {
      flex: 0 0 auto;
      margin-left: 25mm;
      width: 95mm;
      border: 1pt solid #000000;
      padding: 3mm;
    }

    .note__title {
      font-weight: bold;
      font-size: 11pt;
      margin-bottom: 2mm;
    }

    .note__body {
      font-size: 12pt;
      line-height: 1.3;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    @media print {
      .page {
        width: auto;
        height: auto;
      }
    }
`;

export const buildPostalFormDocument = ({
  type,
  deliveryWindow,
  payer,
  recipient,
  sender,
  otkupninaValue = '',
  shipmentNote = '',
}) => {
  const typeLabel = getPostalFormTypeLabel(type);
  const title = typeLabel ? `Vrsta usluge – ${typeLabel}` : 'Vrsta usluge';
  const note = trimValue(shipmentNote);
  const noteHtml = note
    ? `
      <div class="note">
        <div class="note__title">Napomena o pošiljci</div>
        <div class="note__body">${escapeHtml(note)}</div>
      </div>`
    : '';

  const rows = [
    sectionRow(title, 3),
    `
      <tr>
        <td class="label label--small">Danas za  sutra</td>
        <td class="value">Do 12</td>
        <td class="mark">${mark(deliveryWindow === POSTAL_FORM_DELIVERY_WINDOWS.UNTIL_12)}</td>
      </tr>`,
    `
      <tr>
        <td class="label"></td>
        <td class="value">Do 19</td>
        <td class="mark">${mark(deliveryWindow === POSTAL_FORM_DELIVERY_WINDOWS.UNTIL_19)}</td>
      </tr>`,
    sectionRow('Način plaćanja', 2),
    `
      <tr>
        <td class="label">Pošiljalac</td>
        <td class="value"></td>
        <td class="mark">${mark(payer === POSTAL_FORM_PAYERS.SENDER)}</td>
      </tr>`,
    `
      <tr>
        <td class="label">Primalac</td>
        <td class="value"></td>
        <td class="mark">${mark(payer === POSTAL_FORM_PAYERS.RECIPIENT)}</td>
      </tr>`,
    sectionRow('Pošiljalac', 3),
    ...partyRows(sender || POSTAL_FORM_SENDER).map(fieldRow),
    sectionRow('Primalac', 3),
    ...partyRows(recipient).map(fieldRow),
    sectionRow('Opis sadržine', 2),
    `
      <tr>
        <td class="label"></td>
        <td class="value value--centered">${escapeHtml(POSTAL_FORM_CONTENT_DESCRIPTION)}</td>
        <td class="mark"></td>
      </tr>`,
    ...(type === POSTAL_FORM_TYPES.SA_OTKUPNINOM
      ? specialServicesRows({
          otkupninaValue,
          sender: sender || POSTAL_FORM_SENDER,
        })
      : []),
  ].join('');

  return `<!doctype html>
<html lang="sr">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(typeLabel ? `Obrazac za poštu – ${typeLabel}` : 'Obrazac za poštu')}</title>
  <style>${documentStyles}</style>
</head>
<body>
  <div class="page">
    <div class="page__top-half layout">
      <table class="postal-form">
        <colgroup>
          <col style="width: 46mm" />
          <col style="width: 79mm" />
          <col style="width: 15mm" />
        </colgroup>
        <tbody>${rows}
        </tbody>
      </table>${noteHtml}
    </div>
  </div>
</body>
</html>`;
};
